import type { Ref } from 'preact'
import type { ChatConfig, UiTexts } from '../../types/chat'
import styles from './ChatLauncher.module.css'

interface Props {
  config: ChatConfig
  texts: UiTexts
  isOpen: boolean
  onClick: () => void
  buttonRef?: Ref<HTMLButtonElement>
}

const DefaultChatIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <path
      d="M12 3C7.03 3 3 6.58 3 11c0 2.43 1.23 4.61 3.19 6.11-.09.76-.39 2.02-1.31 3.16 0 0 2.06-.21 3.76-1.45.73.2 1.5.31 2.36.31 4.97 0 9-3.58 9-8s-4.03-8-9-8z"
      fill="currentColor"
    />
  </svg>
)

function LauncherIcon({ url }: { url?: string }) {
  if (url) {
    return <img src={url} alt="" className={styles.customIcon} aria-hidden="true" />
  }
  return <DefaultChatIcon />
}

export default function ChatLauncher({ config, texts, isOpen, onClick, buttonRef }: Props) {
  // Closed: the configured label (launcherText) or "Chat mit … öffnen"; open: "Chat schließen".
  const label = isOpen ? texts.closeChat : config.launcherText || texts.openChat
  const variant = config.launcherVariant ?? 'icon'

  const classList = [
    styles.launcher,
    variant === 'text' ? styles.text : '',
    variant === 'icon-text' ? styles.iconText : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <button
      ref={buttonRef}
      type="button"
      className={classList}
      data-part="launcher"
      onClick={onClick}
      aria-label={label}
      aria-expanded={isOpen ? 'true' : 'false'}
      aria-controls="n8n-chatbot-panel"
    >
      {variant === 'icon' && <LauncherIcon url={config.launcherIconUrl} />}
      {variant === 'text' && <span>{config.launcherText || 'Chat'}</span>}
      {variant === 'icon-text' && (
        <>
          <LauncherIcon url={config.launcherIconUrl} />
          <span>{config.launcherText || 'Chat'}</span>
        </>
      )}
    </button>
  )
}
